<template>
  <popup
    v-model="showPopup"
    @confirm="saveSettings"
    :title="t('title')"
    :disabled="locked"
    :loading="loader.on"
    :hide-button-cancel="isDraft"
    :button-confirm-text="isDraft ? t('popupButtons.ok') : t('popupButtons.save')"
    width="650px"
  >
    <div class="auto-shutdown">
      <alert-bar v-if="isDraft" :description="t('saasDraftWarning')" type="warning" />
      <alert-bar v-else-if="locked" :description="t('warningMessage')" type="warning" />

      <p>
        {{ t("description") }}
      </p>

      <v-radio-group v-model="autoShutdownView" :disabled="locked || isDraft" class="auto-shutdown__group" hide-details>
        <v-radio :label="t('radioButton.autoShutdown')" :value="AutoShutdownOption.UPTIME" />
        <v-radio :label="t('radioButton.schedule')" :value="AutoShutdownOption.DATE" />
      </v-radio-group>

      <v-form v-if="isView(AutoShutdownOption.DATE) || isView(AutoShutdownOption.UPTIME)" ref="settingsForm">
        <scheduler-settings v-if="isView(AutoShutdownOption.DATE)" :disabled="locked || isDraft" />

        <timer-settings v-else-if="isView(AutoShutdownOption.UPTIME)" :disabled="locked || isDraft" />
      </v-form>

      <div v-if="!isView(null) && !locked && !isDraft" class="auto-shutdown__control">
        <v-btn @click="clearSettings" :disabled="locked" plain>
          <v-icon left>action_close</v-icon>

          <span>
            {{ t("clearButton." + autoShutdownView) }}
          </span>
        </v-btn>
      </div>
    </div>
  </popup>
</template>

<script lang="ts">
import { defineComponent, watch, computed, ref, onBeforeUnmount } from "@vue/composition-api"
import { createTranslationModule } from "@/plugins/i18n"
import { useStore } from "@/composable/useStore"
import { WorkspaceStatusEnum } from "@/types/workspace"
import { VForm } from "@/types/core"
import { raiseSuccess } from "@/utils/event-bus"
import Popup from "@/components/popup/PopupRoot.vue"
import Utils from "@/utils/utils"
import Loader from "@/utils/loader"
import TimerSettings from "./timer/_TimerSettings.vue"
import SchedulerSettings from "./schedule/_SchedulerSettings.vue"
import AlertBar from "@/components/alert-bar"
import "./auto-shutdown.scss"

enum AutoShutdownOption {
  UPTIME = "uptime",
  DATE = "date"
}

export default defineComponent({
  name: "AutoShutdown",
  props: {
    value: {
      type: Boolean,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    },
    locked: {
      type: Boolean,
      default: false
    }
  },
  setup(props, ctx) {
    const { spaceModule, organizationModule } = useStore()
    const t = createTranslationModule("AutoShutdown")
    const loader = Loader()
    const showPopup = Utils.vModel(props, ctx.emit)
    const autoShutdownView = ref<null | AutoShutdownOption>(null)
    const settingsForm = ref<null | VForm>(null)
    const lastViewUsed = ref<AutoShutdownOption>()

    const isDraft = computed(() => {
      return spaceModule.watching.status === WorkspaceStatusEnum.Draft
    })

    const saveSettings = () => {
      if (isDraft.value) {
        showPopup.value = false
      } else if (settingsForm.value) {
        if (settingsForm.value.validate()) {
          loader.run(async () => {
            const id = spaceModule.watching.id
            await spaceModule.scheduleModule.save(id)

            showPopup.value = false
            _showSaveMessage()
          })
        }
      } else {
        loader.run(async () => {
          const id = spaceModule.watching.id

          spaceModule.scheduleModule.reset()
          await spaceModule.scheduleModule.save(id)

          showPopup.value = false
          _showSaveMessage()
        })
      }
    }

    const isView = (view: AutoShutdownOption | null) => {
      return autoShutdownView.value === view
    }

    const clearSettings = () => {
      spaceModule.scheduleModule.reset()
      autoShutdownView.value = null
    }

    const _loadSavedSettings = () => {
      const shutdownSettings = spaceModule.scheduleModule.temp.shutdownSettings
      const schedule = spaceModule.scheduleModule.temp.scheduledOnOff

      if (shutdownSettings && shutdownSettings.autoShutdownEnabled) {
        autoShutdownView.value = AutoShutdownOption.UPTIME
      } else if (schedule && schedule.scheduledOnOffEnabled) {
        autoShutdownView.value = AutoShutdownOption.DATE
      }
    }

    const _showSaveMessage = () => {
      if (lastViewUsed.value === AutoShutdownOption.DATE) {
        raiseSuccess({ text: t("snackbar.editedSchedule") })
      } else if (lastViewUsed.value === AutoShutdownOption.UPTIME) {
        raiseSuccess({ text: t("snackbar.editedShutdown") })
      }
    }

    loader.run(async () => {
      const id = spaceModule.watching.id

      await spaceModule.scheduleModule.startEdit(id)
      await organizationModule.getOrganizationDetail(spaceModule.watching.organizationId)
      _loadSavedSettings()
    })

    watch(autoShutdownView, () => {
      const schedule = spaceModule.scheduleModule.temp.scheduledOnOff
      const settings = spaceModule.scheduleModule.temp.shutdownSettings

      if (schedule && settings) {
        if (isView(AutoShutdownOption.DATE)) {
          schedule.scheduledOnOffEnabled = true
          settings.autoShutdownEnabled = false
        } else if (isView(AutoShutdownOption.UPTIME)) {
          schedule.scheduledOnOffEnabled = false
          settings.autoShutdownEnabled = true
        } else {
          schedule.scheduledOnOffEnabled = false
          settings.autoShutdownEnabled = false
        }
      }
    })

    watch(autoShutdownView, (view: AutoShutdownOption | null) => {
      if (view) {
        lastViewUsed.value = view
      }
    })

    onBeforeUnmount(() => {
      loader.run(async () => {
        const id = spaceModule.watching.id
        await spaceModule.scheduleModule.updateData(id)
      })
    })

    return {
      t,
      isDraft,
      settingsForm,
      clearSettings,
      isView,
      autoShutdownView,
      AutoShutdownOption,
      saveSettings,
      loader,
      showPopup
    }
  },
  components: {
    AlertBar,
    Popup,
    SchedulerSettings,
    TimerSettings
  }
})
</script>
